import { useCallback, useMemo } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { ApolloCache } from '@apollo/client';

import { useUser } from '%hooks/useUser';

import { getTimeToNow } from '%helpers/date';
import { Haptic } from '%services/haptics';
import { usePostUpdatedSubscription } from '%components/PostComments/__generated__/post.generated';
import {
	PostWithCommentsAndLikesFragment,
	PostWithCommentsAndLikesFragmentDoc,
} from '%fragments/__generated__/postData.generated';
import { PostData } from './types';
import {
	useLikePostMutation,
	useRemoveLikeFromPostMutation,
} from './actions/__generated__/like.mutation.generated';

function updatePostCache(
	cache: ApolloCache<object>,
	postId: PostData['id'],
	updateLogic: (fragmentData: PostWithCommentsAndLikesFragment) => PostWithCommentsAndLikesFragment,
) {
	return cache.updateFragment<PostWithCommentsAndLikesFragment>(
		{
			id: `Post:${postId}`,
			fragment: PostWithCommentsAndLikesFragmentDoc,
			fragmentName: 'PostWithCommentsAndLikes',
		},
		(fragmentData) => {
			if (!fragmentData) {
				return fragmentData;
			}

			return updateLogic(fragmentData);
		},
	);
}

export const usePost = (post: PostData, isInView = false) => {
	const { navigate } = useNavigation();
	const { params } = useRoute();

	usePostUpdatedSubscription({
		skip: !isInView,
		variables: { postUuid: post.uuid },
		onData: ({ client, data: { data } }) => {
			if (!data?.postUpdated) {
				return;
			}

			if (data.postUpdated.newLike) {
				const newLike = data.postUpdated.newLike;

				updatePostCache(client.cache, post.id, (fragmentData) => ({
					...fragmentData,
					likes: [
						...(fragmentData.likes || []).filter((like) => like.author.id !== newLike.author.id),
						newLike,
					],
				}));
			}

			if (data.postUpdated.removedLike) {
				const removedLike = data.postUpdated.removedLike;

				updatePostCache(client.cache, post.id, (fragmentData) => ({
					...fragmentData,
					likes: (fragmentData.likes || []).filter(
						(like) => like.author.id !== removedLike.author.id,
					),
				}));
			}

			if (data.postUpdated.newComment) {
				const newComment = data.postUpdated.newComment;

				updatePostCache(client.cache, post.id, (fragmentData) => ({
					...fragmentData,
					comments: [...(fragmentData.comments || []), newComment],
				}));
			}

			if (data.postUpdated.removedComment) {
				const removedComment = data.postUpdated.removedComment;

				updatePostCache(client.cache, post.id, (fragmentData) => ({
					...fragmentData,
					comments: (fragmentData.comments || []).filter(
						(comment) => comment.id !== removedComment.id,
					),
				}));
			}
		},
	});
	const [likePost, { loading: isLiking }] = useLikePostMutation();
	const [removeLikeFromPost, { loading: isRemovingLike }] = useRemoveLikeFromPostMutation();
	const user = useUser();

	const userLikeId = post.likes?.find((like) => like.author.id === user?.id.toString())?.id;

	const toggleLikePost = useCallback(() => {
		if (isLiking || isRemovingLike) {
			return;
		}

		if (userLikeId) {
			return removeLikeFromPost({
				variables: { likeId: userLikeId },
				optimisticResponse: {
					__typename: 'Mutation',
					removeLikeFromPost: {
						__typename: 'PostLike',
						id: userLikeId,
					},
				},
				update: (cache, result) => {
					const removedLike = result.data?.removeLikeFromPost;
					if (!removedLike) {
						return;
					}

					updatePostCache(cache, post.id, (fragmentData) => ({
						...fragmentData,
						likes: (fragmentData.likes || []).filter((like) => like.id !== removedLike.id),
					}));
				},
			});
		}

		void Haptic.success();

		return likePost({
			variables: { postId: post.id },
			optimisticResponse: {
				__typename: 'Mutation',
				likePost: {
					__typename: 'PostLike',
					id: `temp-like-post:${post.id}`,
					author: {
						__typename: 'User',
						...user,
					},
				},
			},
			update: (cache, result) => {
				const newLike = result.data?.likePost;
				if (!newLike) {
					return;
				}

				updatePostCache(cache, post.id, (fragmentData) => ({
					...fragmentData,
					likes: [
						...(fragmentData.likes || []).filter((like) => like.author.id !== newLike.author.id),
						newLike,
					],
				}));
			},
		});
	}, [isLiking, isRemovingLike, post.id, userLikeId, likePost, removeLikeFromPost, user]);

	const publicationTime = useMemo(() => {
		return getTimeToNow(post.publishedAt || post.createdAt);
	}, [post.publishedAt, post.createdAt]);

	const onAllCommentsClick = () => {
		if (params?.uuid) {
			return navigate('Community', {
				screen: 'CommunityPost',
				params: {
					postUuid: post.uuid,
					uuid: params.uuid,
				},
			});
		}

		if (params?.feedId && params?.userId && params?.folderId) {
			const { feedId, folderId, userId } = params;

			return navigate('InformationFeedPost', {
				postUuid: post.uuid,
				feedId,
				folderId,
				userId,
			});
		}

		navigate('PostComments', {
			postUuid: post.uuid,
		});
	};

	const onLikesClick = () => {
		if (!post.likes) {
			return;
		}

		navigate('Likes', {
			likes: post.likes,
		});
	};

	const onAvatarPress = () => {
		if (!post.author) {
			return;
		}

		navigate('Profile', {
			userId: post.author.id,
		});
	};

	return {
		isLiked: !!userLikeId,
		disabledLike: isLiking || isRemovingLike,
		likesCount: post.likes?.length || 0,
		toggleLikePost,
		publicationTime,
		onAllCommentsClick,
		onLikesClick,
		onAvatarPress,
	};
};
